import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useDfcThemeMode } from '../../../context/DfcThemeModeContext';
import { ASA_COLOR, ASA_LOADER_LABEL } from '../../../Utils/asaBrand';
import { ASA_BRACKET_LEFT, ASA_BRACKET_RIGHT } from './AsaLogoMark';

const closeLeft = keyframes`
  0%, 100% { transform: translateX(-7px); opacity: 0.6; }
  50% { transform: translateX(0); opacity: 1; }
`;
const closeRight = keyframes`
  0%, 100% { transform: translateX(7px); opacity: 0.6; }
  50% { transform: translateX(0); opacity: 1; }
`;

const Screen = styled('div', {
  shouldForwardProp: prop => prop !== 'dark' && prop !== 'fullScreen',
})<{ dark: boolean; fullScreen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: ${({ fullScreen }) => (fullScreen ? '100vh' : '60vh')};
  /* The page's own background, so nothing changes colour around the mark. */
  background: ${({ dark }) => (dark ? ASA_COLOR.navy : ASA_COLOR.white)};
  color: ${({ dark }) => (dark ? ASA_COLOR.white : ASA_COLOR.royalBlue)};

  svg {
    width: auto;
    height: 64px;
    overflow: visible;
  }
  .asa-bracket-left,
  .asa-bracket-right {
    transform-box: fill-box;
  }
  .asa-bracket-left {
    animation: ${closeLeft} 1.2s ease-in-out infinite;
  }
  .asa-bracket-right {
    animation: ${closeRight} 1.2s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .asa-bracket-left,
    .asa-bracket-right {
      animation: none;
    }
  }
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

interface AsaPageLoaderProps {
  /** The whole viewport (before the company loads), or the page area (while a page's code loads) */
  fullScreen?: boolean;
}

/**
 * All Sports Arena's loader: the square brackets "[ ]" from ASA's wordmark, closing in, on the
 * page's own background (white in light mode, navy in dark) so the page never changes colour
 * around it. Royal blue on white (7.34:1), white on navy. Static under `prefers-reduced-motion`.
 * Like DFC's loader it needs nothing from the company record.
 */
const AsaPageLoader: React.FC<AsaPageLoaderProps> = ({ fullScreen = false }) => {
  const { mode } = useDfcThemeMode();
  return (
    <Screen
      dark={mode === 'dark'}
      fullScreen={fullScreen}
      role="status"
      aria-live="polite"
      data-testid="asa-page-loader"
    >
      <svg viewBox="104 -2 36 48" fill="currentColor" aria-hidden="true">
        <path className="asa-bracket-left" d={ASA_BRACKET_LEFT} />
        <path className="asa-bracket-right" d={ASA_BRACKET_RIGHT} />
      </svg>
      <VisuallyHidden>{ASA_LOADER_LABEL}</VisuallyHidden>
    </Screen>
  );
};

export default AsaPageLoader;
